import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { useAsyncValue, useNavigate, useRevalidator } from 'react-router-dom';
import { useRevalidateOnSync } from '../../../../../../offline/useRevalidateOnSync';
import { format } from 'date-fns';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Types
import { NewCommunication } from '../../../../../../types/communication';
import { OrderType } from '../../../../../../types/orders';
import { Role } from '../../../../../../types/auth';

// Hooks
import { useAuth } from '../../../../../../hooks/useAuth';

// Components
import ContentCardResume from '../../../../../../components/display/ContentCardResume';
import { Typography } from '../../../../../../components/display/Typography';
import { MainButton } from '../../../../../../components/inputs/buttons/MainButton';

// Styles
import { Container } from './styles';

// Redux
import { roidOTSelected } from '../../../../../../state/actions/ui/roidOTSelectedGeneralView';
import { tabs } from '../../../../../../state/actions/ui/tabs';
import { tabsType } from '../../../../../../state/reducers/ui/tabs';

// Utils
import { decompressSessionStorage } from '../../../../../../utils/sessionStorage';
import { isInternalPath } from '../../../../../../utils/internalPath';
import { formNaming as formCommunicationNaming } from '../../../../../../components/forms/NewCommunicationForm/constants';
import { getClassificationName } from '../../../../../../utils/orders';

export const CommunicationSummaryGeneralView = (): JSX.Element => {
  const { t } = useTranslation();
  const loaderData = useAsyncValue() as NewCommunication;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  useRevalidateOnSync();

  // Get current user role - Tablet cannot change communication state
  const { user } = useAuth();
  const currentRole = user?.user?.rolActive as Role | undefined;

  // Read navigation context from session storage
  const navigationContext = useMemo(() => {
    const raw = sessionStorage.getItem(
      formCommunicationNaming.COMMUNICATION_NAVIGATION_CONTEXT
    );
    if (!raw) return null;
    try {
      return JSON.parse(decompressSessionStorage(raw));
    } catch {
      return null;
    }
  }, []);

  const navigationOrigin = navigationContext?.origin || 'communications-table';
  const parentOrderId = navigationContext?.parentOrderId;
  const parentRoid = navigationContext?.parentRoid;
  const returnTo = navigationContext?.returnTo as string | undefined;

  // Detect if parent order is a campaign
  const isCampaign = navigationContext?.parentOrderType === OrderType.Campaign
    || parentRoid?.startsWith('Ca');

  // Loader fallido: el selector lanzó (offline sin cache, 404, 403…) o devolvió
  // un payload sin id reconocible. Protegemos render Y effects para evitar
  // crash. Detalle: docs/superpowers/plans/2026-05-12-offline-edit-action-accident.md § Fase 3.
  const loaderFailed = !loaderData || !loaderData.id;

  useLayoutEffect(() => {
    if (loaderFailed) return;
    dispatch(
      roidOTSelected(
        OrderType.Communication,
        Array.isArray(loaderData.roid)
          ? loaderData.roid.join('.')
          : loaderData.roid,
        loaderData.id as string
      )
    );
  }, [loaderData, loaderFailed]);

  const reftechData = () => {
    revalidator.revalidate();
  };

  // Context-aware redirect handler
  const handleBackNavigation = useCallback(() => {
    // Clean up navigation context
    sessionStorage.removeItem(formCommunicationNaming.COMMUNICATION_NAVIGATION_CONTEXT);

    // Context-aware redirect
    if (navigationOrigin === 'workorder' && parentOrderId) {
      const targetRoute = isCampaign
        ? `/work-orders/campaign/${parentOrderId}`
        : `/work-orders/${parentOrderId}`;
      navigate(targetRoute);
    } else if (isInternalPath(returnTo)) {
      // Espejo de `AccidentPageSummary`: si se llegó aquí desde la pestaña de
      // una campaña ("Ver comunicación"), volver a ESA pestaña y no a la vista
      // global.
      navigate(returnTo);
    } else {
      // Ruta ABSOLUTA + índice de pestaña, igual que `AccidentPageSummary`.
      // `navigate('..')` resolvía contra la RUTA (`communications/:id` bajo
      // `work-orders`), no contra la URL, así que dejaba al usuario en "Vista
      // previa de O.T." en vez de en la pestaña Comunicaciones de la que
      // había salido. Se veía poco porque hasta 2026-08-05 casi nadie
      // aterrizaba aquí; ahora es el destino del guardado de una edición.
      dispatch(tabs(tabsType.WORK_ORDERS_GENERAL, 2));
      navigate('/work-orders/communications');
    }
  }, [navigationOrigin, parentOrderId, isCampaign, returnTo, navigate, dispatch]);

  if (loaderFailed) {
    const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
    const titleKey = isOffline
      ? 'communications:errors.communicationOfflineNotCached'
      : 'communications:errors.communicationNotFound';
    const descKey = isOffline
      ? 'communications:errors.communicationOfflineNotCachedDescription'
      : 'communications:errors.communicationNotFoundDescription';
    return (
      <Container>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <Typography size="L" variant="semiBold" style={{ color: '#d32f2f' }}>
            {t(titleKey)}
          </Typography>
          <Typography size="M" style={{ marginTop: '0.5rem', color: '#666' }}>
            {t(descKey)}
          </Typography>
          <MainButton
            text={t('buttons:back', 'Volver')}
            onClick={() => navigate(-1)}
            style={{ marginTop: '1rem' }}
          />
        </div>
      </Container>
    );
  }

  return (
    <Container>
      {loaderData && (
        <ContentCardResume
          title={t('communications:summary')}
          roid={loaderData.roid}
          operator={loaderData.operator}
          data={format(
            new Date(loaderData.registerInitDate as string),
            'dd / MM / yyyy'
          )}
          hour={format(
            new Date(loaderData.registerInitDate as string),
            'HH:mm'
          )}
          groupInter={loaderData.groupInter}
          media={loaderData.media}
          sendStatus={loaderData.sendStatus}
          capitol={loaderData.capitol}
          subCapitol={loaderData.subCapitol}
          classification={getClassificationName(
            loaderData.classification,
            loaderData.classifications
          )}
          predefine={loaderData.predefine}
          team={loaderData.team}
          concession={loaderData.concession}
          road={loaderData.road}
          pkInit={`${loaderData.pkInit} km`}
          pkEnd={`${loaderData.pkEnd} km`}
          track={loaderData.track}
          roadDirections={loaderData.roadDirections}
          direction={loaderData.direction}
          city={loaderData.city}
          lane={loaderData.lane}
          margin={loaderData.margin}
          leftButton={t('buttons:back')}
          onEditCommunication={handleBackNavigation}
          description={loaderData.description}
          notes={loaderData.notes}
          storedDataOTSelected={loaderData.storedDataOTSelected}
          orderType={OrderType.Communication}
          changeStateTag={currentRole !== Role.Tablet}
          revalidatorFunction={reftechData}
          state={loaderData.state}
          orderId={loaderData.id}
          communicationId={loaderData.communicationID}
        />
      )}
    </Container>
  );
};
